import type { RuntimeConfig } from "nuxt/schema";
import type { AuthLoginOptions, PROVIDER_NAMES, IAuthProvider } from "../types";
import type TestAuth from "../providers/test-auth";
import { AUTH_PROVIDER_STORAGE_KEY, PROVIDERS, PROVIDER_KEYS } from "../utils/constants";
import BaseAuthClass from "../utils/base-auth-class";

export default class Auth extends BaseAuthClass {
  #currentProvider: BaseAuthClass | undefined;
  #providerPromise: Promise<IAuthProvider<BaseAuthClass>> | undefined;
  #ready: boolean;
  #readyPromise: Promise<void>;
  #readyResolve: (value: void | PromiseLike<void>) => void;

  constructor(config: RuntimeConfig) {
    super(config);
    this.#currentProvider = undefined;
    this.#providerPromise = undefined;
    this.#ready = false;
    this.#readyResolve = () => {};
    this.#readyPromise = new Promise<void>((res) => {
      this.#readyResolve = res;
    });
    // Wrapping to check for prod so that it will compile out correctly
    // only during testing the test provider most be set on the constructor, any other provider will be determined dynamically
    if (import.meta.env.DANGEROUSLY_BUILD_TEST_AUTH) {
      PROVIDERS["test"] = {
        validate: (_) => import.meta.env.DANGEROUSLY_BUILD_TEST_AUTH,
        import: async () => (await import("../providers/test-auth")).default as IAuthProvider<TestAuth>,
      };
      PROVIDER_KEYS.push("test");
      this.provider = "test";
    }
  }

  get validProviders() {
    if (!this.config) return [];
    return PROVIDER_KEYS.filter((name) => PROVIDERS[name]!.validate(this.config));
  }

  get authFlow() {
    return this.#currentProvider?.authFlow || "idle";
  }

  get authValidUntil() {
    return this.#currentProvider?.authValidUntil ?? undefined;
  }

  get provider() {
    let provider = (localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) || "") as PROVIDER_NAMES | "";
    if (provider !== "" && !this.validProviders.includes(provider)) {
      const badProvider = provider;

      this.clearData().then(() => {
        devConsole.warn(`🔒 ckAuth: ${badProvider} is not a valid auth provider!`);
        this.broadcastEvent({
          type: "auth-provider-error",
          detail: new Error(`${badProvider} is not a valid auth provider!`),
        });
      });
      provider = "";
    }
    return provider;
  }

  set provider(value: PROVIDER_NAMES | "") {
    if (value === "" || this.validProviders.includes(value)) {
      localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, value);
    }
  }

  async isReady() {
    await this.#readyPromise;
    return this.#ready;
  }
  async initialize() {
    // if we have already started the provider.
    if (this.#currentProvider) {
      this.#ready = true;
      this.#readyResolve();
      return;
    }

    if (this.provider === "") {
      // if there are no providers selected, pick the first valid one
      this.provider = this.validProviders[0] || this.provider;
    }
    if (this.provider !== "" && this.validProviders.includes(this.provider)) {
      this.#providerPromise = PROVIDERS[this.provider]!.import();
      try {
        const AuthProviderClass = await this.#providerPromise;
        this.#currentProvider = new AuthProviderClass(this.config) as InstanceType<typeof AuthProviderClass>;
        this.#currentProvider.addEventCallback(this.broadcastEvent.bind(this));
        await this.#currentProvider.initialize();
      } catch (e) {
        this.#readyResolve();
        devConsole.error(e);
        this.broadcastEvent({
          type: "auth-initialize-error",
          detail: e as Error,
        });
        await this.clearData();
        window.location.href = "/";
      }
    }
    this.#ready = true;
    this.#readyResolve();
  }

  async login(options?: AuthLoginOptions) {
    await this.#readyPromise;
    if (this.#currentProvider) {
      try {
        return await this.#currentProvider.login(options);
      } catch (e) {
        devConsole.error("🔒 ckAuth: login error", e);
        await this.clearData();
        this.broadcastEvent({
          type: "auth-login-error",
          detail: e as Error,
        });

        if (e instanceof $ck.utils.UserFriendlyError) {
          throw e;
        }
        return false;
      }
    }
    return false;
  }

  async logout() {
    await this.#readyPromise;
    this.provider = "";
    this.#providerPromise = undefined;
    if (this.#currentProvider) {
      await this.#currentProvider.logout();
      this.#currentProvider.clearEventCallbacks();
    }

    this.#currentProvider = undefined;
    return true;
  }

  override async resetPassword(payload: { token: string; newPassword: string }) {
    await this.#readyPromise;
    if (!this.#currentProvider) {
      throw new Error("No current provider available");
    }
    return await this.#currentProvider.resetPassword(payload);
  }

  async refreshToken() {
    await this.#readyPromise;
    if (this.#currentProvider) {
      return this.#currentProvider.refreshToken();
    }
    return "";
  }

  async getToken() {
    await this.#readyPromise;
    if (this.#currentProvider) {
      return this.#currentProvider.getToken();
    }
    return "";
  }

  async getAccounts() {
    await this.#readyPromise;
    if (this.#currentProvider) {
      return this.#currentProvider.getAccounts();
    }
    return [];
  }

  async isAuthed() {
    if (this.#currentProvider) {
      return this.#currentProvider.isAuthed();
    }
    return false;
  }

  async clearData() {
    if (this.#currentProvider) {
      await this.#currentProvider.clearData();
    } else {
      window.localStorage.clear();
    }
  }

  async resetAuthFlow() {
    if (this.#currentProvider) {
      await this.#currentProvider.resetAuthFlow();
    }
  }

  async handleAuthError(error: Error): Promise<boolean> {
    if (!(await this.#currentProvider?.handleAuthError(error))) {
      await this.clearData();
      return false;
    }
    return true;
  }
}
